<template>

  <div class="container-fluid px-4 rounded-4 contenedor-principal">
    <div class="grid-header">
      <h1>Importaciones</h1>
    </div>
    <div class=" bg-white p-4 rounded-4 mb-3 border">

      <div class="d-flex align-items-center gap-3 flex-wrap">

        <DxSelectBox v-model="selectedCompany" v-bind="selectBoxProps" />
        <div class="d-flex align-items-center gap-2">
          <DxSwitch v-model:value="esSimulacion" /> Simular importación
        </div>
        <BotoneraImportacionesEndesa v-if="selectedCompany === GrupoEmpresaEnergiaEnum.Endesa" @result:modified="onImportResult" :esSimulacion="esSimulacion" />
        <BotoneraImportacionesNaturgy v-else-if="selectedCompany === GrupoEmpresaEnergiaEnum.Naturgy" @result:modified="onImportResult"
          :esSimulacion="esSimulacion" />
      </div>
    </div>
    <div class="bg-white p-4 rounded-4 border" v-if="importResult">
      <div class="title-bar-container d-flex justify-content-between mb-1">
        <div class="d-flex align-items-center gap-2">
          <h5>Resultado de la importación</h5>
          <span class="importacion-simulada-indicador" v-if="importResult.EsSimulacion">IMPORTACIÓN SIMULADA</span>
        </div>
        <DxButton v-if="importResult.FicheroSimulacionBase64" text="Descargar fichero simulación" icon="download" type="default" @click="descargarFicheroSimulacion" />
      </div>
      <div class="resultados-importacion mb-2"> {{ importResult.RegistrosProcesadosCorrectamente.length }} registros procesados.</div>
      <div class="resultados-importacion-container">
        <div v-for="item in resumenItems" :key="item.type" :class="['resultados-importacion-item', item.type]">
          <div class="icon-wrapper">
            <i :class="item.icon"></i>
          </div>
          <div class="data-item-importacion">
            <span class="number">{{ item.count }}</span>
            <span class="text">{{ item.label }}</span>
          </div>
        </div>
      </div>
      <hr />
      <div>
        <div class="title-bar-container mb-2">
          <h5>Detalle</h5>
        </div>
        <DxAccordion :data-source="accordionItems" :collapsible="true" :multiple="true" :selected-index="2" item-title-template="title">
          <template #title="{ data }">
            <span :class="['accordion-title', data.type]">
              <i :class="data.icon"></i>
              {{ data.title }}
            </span>
          </template>
          <template #item="{ data }">
            <div class="accordion-content">
              <template v-if="data.type === 'omitidos'">
                <div v-if="importResult.EsSimulacion">
                  <ul v-if="data.items.length > 0">
                    <li v-for="(registro, index) in data.items" :key="index">
                      <strong>Fila {{ registro.Fila }}:</strong> {{ registro.Mensaje }}
                    </li>
                  </ul>
                  <p v-else>No hay registros</p>
                </div>
                <div v-else><strong>Filas: </strong>{{importResult?.RegistrosOmitidos.map(r => r.Fila)}}</div>
              </template>
              <template v-else>
                <ul v-if="data.items.length > 0">
                  <li v-for="(registro, index) in data.items" :key="index">
                    <strong>Fila {{ registro.Fila }}:</strong> {{ registro.Mensaje }}
                  </li>
                </ul>
                <p v-else>No hay registros</p>
              </template>
            </div>
          </template>
        </DxAccordion>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { DxSelectBox, DxSwitch, DxAccordion, DxButton } from "devextreme-vue";
import { saveAs } from "file-saver";
import { GrupoEmpresaEnergiaEnum } from "@api/api";
import { getTranslatedListApiEnum } from "@helpers/enums-helper";
import { getSelectBoxDefaultProperties } from "@helpers/grid-helper";
import type { ImportResultDto } from "@api/api";
import BotoneraImportacionesEndesa from "@/components/importaciones/BotoneraImportacionesEndesa.vue";
import BotoneraImportacionesNaturgy from "@/components/importaciones/BotoneraImportacionesNaturgy.vue";

const GRUPOS_CON_IMPORTACION = [GrupoEmpresaEnergiaEnum.Endesa.toString(), GrupoEmpresaEnergiaEnum.Naturgy.toString()];

const selectBoxProps: DxSelectBox = {
  ...getSelectBoxDefaultProperties(),
  dataSource: getTranslatedListApiEnum(GrupoEmpresaEnergiaEnum).filter(item => GRUPOS_CON_IMPORTACION.includes(item.value)),
  displayExpr: "text",
  valueExpr: "value",
  placeholder: "Seleccione grupo"
};

const selectedCompany = ref<string | null>(null);
const importResult = ref<ImportResultDto | null>(null);
const esSimulacion = ref(true);

function onImportResult(result: ImportResultDto) {
  importResult.value = result;
}

function descargarFicheroSimulacion() {
  if (!importResult.value?.FicheroSimulacionBase64) return;
  const byteCharacters = atob(importResult.value.FicheroSimulacionBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  saveAs(blob, importResult.value.NombreFicheroSimulacion ?? "resultado_simulacion.xlsx");
}

const resumenItems = computed(() => {
  if (!importResult.value) return [];
  return [
    {
      type: "correctos",
      icon: "fa fa-circle-check",
      count: importResult.value.RegistrosProcesadosCorrectamente.length,
      label: "correctos"
    },
    {
      type: "omitidos",
      icon: "fa fa-circle-minus",
      count: importResult.value.RegistrosOmitidos.length,
      label: "omitidos"
    },
    {
      type: "advertencia",
      icon: "fa fa-circle-exclamation",
      count: importResult.value.RegistrosAdvertencia.length,
      label: "advertencia"
    },
    {
      type: "fallidos",
      icon: "fa fa-circle-xmark",
      count: importResult.value.RegistrosFallidos.length,
      label: "fallidos"
    }
  ];
});

const accordionItems = computed(() => {
  if (!importResult.value) return [];
  return [
    {
      title: `Registros omitidos (${importResult.value.RegistrosOmitidos.length})`,
      type: "omitidos",
      icon: "fa fa-circle-minus",
      items: importResult.value.RegistrosOmitidos
    },
    {
      title: `Registros con advertencia (${importResult.value.RegistrosAdvertencia.length})`,
      type: "advertencia",
      icon: "fa fa-circle-exclamation",
      items: importResult.value.RegistrosAdvertencia
    },
    {
      title: `Registros fallidos (${importResult.value.RegistrosFallidos.length})`,
      type: "fallidos",
      icon: "fa fa-circle-xmark",
      items: importResult.value.RegistrosFallidos
    }
  ];
});
</script>

<style scoped lang="scss">

.importacion-simulada-indicador {
  display: flex;
  padding: var(--tag-vertical-padding, 2px) var(--tag-horizontal-padding, 16px);
  justify-content: center;
  align-items: center;
  border-radius: var(--tag-radius, 6px);
  border: 1px solid var(--border-secondary-default, #00AF9F);
  background: var(--background-secondary-default, #00AF9F);
  color: var(--text-secondary-on-action, #FFF);
  font-size: var(--font-size-body-sm, 12px);
  line-height: var(--line-height-body-sm, 18px);
}

.resultados-importacion {
  color: var(--text-default-caption, #6C757D);
  font-size: var(--font-size-body-md, 14px);
}

.resultados-importacion-container {
  display: flex;
  align-items: flex-end;
  gap: var(--spacing-xs, 8px);
  align-self: stretch;
  flex-wrap: wrap;

  .resultados-importacion-item {
    display: flex;
    padding: var(--spacing-xs, 8px) var(--spacing-md, 16px);
    justify-content: center;
    align-items: center;
    gap: var(--spacing-xs, 12px);
    border-radius: var(--border-radius-lg, 8px);
    line-height: 1;

    .icon-wrapper {
      i {
        font-size: var(--icon-size-md, 24px);
      }
    }

    .data-item-importacion {
      display: flex;
      align-items: center;
      gap: var(--spacing-2xs, 4px);

      .number {
        font-size: var(--font-size-body-lg, 18px);
        font-weight: 700;
        line-height: var(--line-height-body-lg, 32px);
      }

      .text {
        font-size: var(--font-size-body-md, 14px);
        font-weight: 700;
      }
    }

    &.correctos {
      border: 1px solid var(--icon-secondary-default, #00AF9F);
      background: var(--background-secondary-light, #B0E6E1);
      color: var(--text-secondary-hover, #007C71);
    }

    &.omitidos,
    &.advertencia {
      border: 1px solid var(--border-warning-default, #EEB737);
      background: var(--background-warning-light, #FFECB2);
      color: var(--text-warning-default, #B58905);
    }


    &.fallidos {
      border: 1px solid var(--border-error-default, #E65858);
      background: var(--background-error-light, #F1BABD);
      color: var(--text-error-hover, #96181E);
    }
  }
}

.accordion-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 8px);
  font-weight: 600;

  &.omitidos,
  &.advertencia {
    color: var(--text-warning-default, #B58905);
  }

  &.fallidos {
    color: var(--text-error-hover, #96181E);
  }

  i {
    font-size: var(--icon-size-sm, 16px);
  }
}

.accordion-content {
  padding: var(--spacing-sm, 12px);

  ul {
    margin: 0;
    padding-left: 0px;
    list-style: none;
  }

  li {
    margin-bottom: var(--spacing-2xs, 4px);
  }
}
</style>
